import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import api from "../Api";
import TableFooter from "../components/TableFooter";
import Popup from "../components/PopUP";
import { Link, useNavigate } from "react-router-dom";
import { IMAGE_URL } from "../utils/constants";
import DeleteModal from "../components/DeleteModal";


const PAGE_SIZE = 20;
const AIRPORT_URL = "/airplane-service/airport/admin";

export default function AirportManagement() {
    const [airports, setAirports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [popupMessage, setPopupMessage] = useState("");

    const [pagination, setPagination] = useState({
        count: 0,
        total_pages: 0,
        next: 0,
        previous: 0,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [query, setQuery] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const navigate = useNavigate();

    // Debounce search -> backend `q`
    useEffect(() => {
        const t = setTimeout(() => {
            setCurrentPage(1); // Reset to page 1 on new search
            setQuery(searchInput.trim());
        }, 400); // 400ms debounce
        return () => clearTimeout(t);
    }, [searchInput]);

    // Popup handling on loading state change
    useEffect(() => {
        if (popupMessage === "") {
            setPopupMessage(
                loading ? "Airports loading, please wait ..." : "Airports loaded successfully"
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    // Fetch Data
    const fetchData = useCallback(async () => {
        let cancelled = false;
        setLoading(true);
        try {
            // Constructing URL exactly as your cURL
            const url = `${AIRPORT_URL}?q=${encodeURIComponent(query)}&page=${currentPage}&page_size=${PAGE_SIZE}&max_page_size=${PAGE_SIZE}`;
            const response = await api.get(url);

            if (cancelled) return;

            const data = response.data;
            setAirports(data.results || []);
            setPagination({
                count: data.count ?? 0,
                total_pages: data.total_pages ?? 0,
                next: data.next ?? 0,
                previous: data.previous ?? 0,
            });
        } catch (error) {
            if (cancelled) return;
            console.error("Airport API Error:", error.message);
            setPopupMessage("Failed to load airport data");
        }
        if (!cancelled) setLoading(false);

        return () => { cancelled = true; };
    }, [currentPage, query]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);


    //  handle delete 
    const [showDeleteModal, setDeleteShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const handleDeleteClick = (item) => {
        setSelectedItem(item);
        setDeleteShowModal(true);
    };

    const handleConfirmDelete = async () => {
        console.log("HandleConfirmDelete ", selectedItem.id);
        try {
            const response = await api.delete(`/airplane-service/airport/${selectedItem.id}`);
            console.log("delete response:", response.data);
            setPopupMessage(response.data.detail || "Airport deleted successfully")

            setSelectedItem(null);
        }
        catch (error) {
            console.log("error: ", error);
            setPopupMessage("An error occurred during deleting");
            return;
        }

        setDeleteShowModal(false);
        setLoading(true);

    }

    return (
        <div className="user-management-page">
            <Popup message={popupMessage} onClose={() => setPopupMessage("")} />
            <Sidebar />

            <div className="main-wrapper">
                <Header />

                <div className="content-wrapper">
                    <section className="main-section spacer-y">
                        <div className="container">
                            <div className="row gy-4">
                                <div className="col-xl-12">

                                    {/* Heading */}
                                    <div className="common_main_bg_wrapper">
                                        <div className="common_main_heading_wrapper">
                                            <div className="vstack">
                                                <h1>Airport Management</h1>
                                            </div>
                                            <div className="d-flex flex-end">
                                                <Link to="/upload-airports" className="secondary_btn mx-2">Upload JSON</Link>
                                                <Link to="/airport-create" className="secondary_btn ">Add Airport</Link>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="common-table-wrapper mt-4">

                                        {/* Filters (Moved outside loading check to prevent input focus loss) */}
                                        <div className="common-table-filter-wrapper">
                                            <div className="common-left-blk">
                                                <div className="common_sub_heading_wrapper mb-0">
                                                    <h2>
                                                        Airport Listing
                                                    </h2>
                                                </div>
                                            </div>
                                            <div className="common-right-blk">
                                                <div className="common-sort-blk">
                                                    <div className="common-search-blk">
                                                        <input
                                                            type="search"
                                                            className="form-control"
                                                            id="search"
                                                            placeholder="Search airport, city, IATA..."
                                                            value={searchInput}
                                                            onChange={(e) => setSearchInput(e.target.value)}
                                                            name="airport_search"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Table Area */}
                                        {loading ? (
                                            <div
                                                className="d-flex justify-content-center align-items-center"
                                                style={{ minHeight: "300px" }}
                                            >
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="table-blk table-responsive">
                                                    <table className="table">
                                                        <thead>
                                                            <tr>
                                                                <th>S.No.</th>
                                                                <th>Airport Name</th>
                                                                <th>IATA / ICAO</th>
                                                                <th>City / Country</th>
                                                                <th>Type</th>
                                                                <th>Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {airports.length > 0 ? (
                                                                airports.map((airport, index) => (
                                                                    <tr key={airport.id || index}>
                                                                        <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                                                                        <td className="text-capitalize">
                                                                            {airport.name || "-"}
                                                                        </td>
                                                                        <td>
                                                                            <span className="badge bg-light text-dark me-1">
                                                                                {airport.iata_code || "N/A"}
                                                                            </span>
                                                                            <span className="badge bg-light text-dark">
                                                                                {airport.icao || "N/A"}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            {airport.city || "-"} <br />
                                                                            <small className="text-muted">{airport.country || "-"}</small>
                                                                        </td>
                                                                        <td>{airport.type || "-"}</td>

                                                                        {/* Action Buttons */}
                                                                        <td className="align-middle">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <button
                                                                                    onClick={() => navigate(`/airport/${airport.id}`)}
                                                                                    className="border-0 bg-transparent p-0"
                                                                                    title="View Details"
                                                                                >
                                                                                    <img src={IMAGE_URL + "eye-tbl-icon.svg"} alt="View" />
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => navigate(`/airport/update/${airport.id}`)}
                                                                                    className="border-0 bg-transparent p-0"
                                                                                    title="Edit"
                                                                                >
                                                                                    <img src={IMAGE_URL + "edit-icon.svg"} alt="Edit" />
                                                                                </button>

                                                                                <button
                                                                                    className="border-0 bg-white"
                                                                                    onClick={() => handleDeleteClick({ "id": airport.id, "name": airport.name || "-" })}
                                                                                >
                                                                                    <img src={IMAGE_URL + "trash-icon.svg"} alt="Trash" />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="6" className="text-center text-muted py-4">
                                                                        No airport records found
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Backend-driven pagination */}
                                                <TableFooter
                                                    pagination={pagination}
                                                    currentPage={currentPage}
                                                    setCurrentPage={setCurrentPage}
                                                />
                                            </>
                                        )}
                                    </div>

                                    <DeleteModal
                                        show={showDeleteModal}
                                        onClose={() => setDeleteShowModal(false)}
                                        onConfirm={handleConfirmDelete}
                                        itemName={selectedItem ? selectedItem.name : ""}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}